import { useEffect } from 'react'
import { Alert, Descriptions, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { ShareAmount, SignedAmount } from './ShareAmount'
import { approveDistribution, bpToPercent, type Distribution } from './stakeholderApi'

interface DistributionApproveModalProps {
  distribution: Distribution | null
  onClose: () => void
}

interface FormValues {
  amount: Money | null
  note?: string
}

/** Tasdiq: Dr 3030 Foyda taqsimoti / Cr 2060 To'lanadigan foyda. Summa hisoblangandan farq qilsa asos majburiy. */
export function DistributionApproveModal({ distribution, onClose }: DistributionApproveModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const amount = Form.useWatch('amount', form)
  const changed = distribution !== null && amount != null && amount.amount !== distribution.calculated.amount

  useEffect(() => {
    if (!distribution) return
    form.setFieldsValue({ amount: distribution.calculated, note: '' })
  }, [distribution, form])

  const approve = useMutation({
    mutationFn: (values: FormValues) => {
      if (!distribution) throw new Error('distribution')
      const approved = values.amount?.amount ?? distribution.calculated.amount
      const note = values.note?.trim() ? values.note.trim() : null
      return approveDistribution(distribution.id, approved === distribution.calculated.amount ? null : approved, note)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      onClose()
    },
  })

  const close = () => {
    approve.reset()
    onClose()
  }

  return (
    <Modal
      open={distribution !== null}
      title={distribution?.kind === 'LOSS' ? t('stakeholder.approve.lossTitle') : t('stakeholder.approve.title')}
      okText={t('stakeholder.action.approve')}
      cancelText={t('common.cancel')}
      confirmLoading={approve.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => approve.mutate(values))}
      destroyOnHidden
    >
      <Alert
        type={distribution?.kind === 'LOSS' ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
        message={distribution?.kind === 'LOSS' ? t('stakeholder.approve.lossNote') : t('stakeholder.approve.note')}
      />
      {approve.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(approve.error, t)} />
      ) : null}
      {distribution ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('stakeholder.distribution.stakeholder')}>
            {distribution.stakeholder.code} · {distribution.stakeholder.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('stakeholder.distribution.netProfit')}>
            <SignedAmount value={distribution.netProfit} />
          </Descriptions.Item>
          <Descriptions.Item label={t('stakeholder.distribution.share')}>
            {t('stakeholder.percent', { value: bpToPercent(distribution.shareBp) })}
          </Descriptions.Item>
          <Descriptions.Item label={t('stakeholder.distribution.calculated')}>
            <ShareAmount value={distribution.calculated} kind={distribution.kind} />
          </Descriptions.Item>
        </Descriptions>
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={approve.isPending}>
        <Form.Item
          name="amount"
          label={t('stakeholder.approve.amount')}
          rules={[
            {
              validator: (_, value: Money | null) =>
                value && value.amount > 0 ? Promise.resolve() : Promise.reject(new Error(t('stakeholder.validation.amount'))),
            },
          ]}
        >
          <MoneyInput minMinor={1} />
        </Form.Item>
        <Form.Item
          name="note"
          label={t('stakeholder.approve.basis')}
          rules={changed ? [{ required: true, whitespace: true, message: t('stakeholder.validation.basis') }] : []}
        >
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
